import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Clock, CheckCircle, XCircle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { RequestQueue } from '@/components/edit-requests/RequestQueue';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { useEditRequestCounts } from '@/hooks/useEditRequestCounts';

export const Route = createFileRoute('/_app/edit-requests')({
  component: () => (
    <RoleGuard allowedRoles={['supervisor', 'owner', 'operator']}>
      <EditRequestsPage />
    </RoleGuard>
  ),
});

function EditRequestsPage() {
  const { farmId, isLoading: farmLoading } = useCurrentFarm();
  const [activeTab, setActiveTab] = useState('pending');

  // Fetch counts for badge display
  const { data: countsData } = useEditRequestCounts(farmId);

  const counts = countsData?.data || { pending: 0, approved: 0, rejected: 0 };

  if (!farmId) {
    return farmLoading ? <div className="flex justify-center py-12">Loading...</div> : null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Edit Requests</h1>
        <p className="text-gray-500">Review and manage data edit requests from Lab Technicians</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="pending">
            <span className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending
              {counts.pending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                  {counts.pending}
                </span>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="approved">
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Approved
              {counts.approved > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  {counts.approved}
                </span>
              )}
            </span>
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <span className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Rejected
              {counts.rejected > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                  {counts.rejected}
                </span>
              )}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <RequestQueue farmId={farmId} status="pending" />
        </TabsContent>

        <TabsContent value="approved">
          <RequestQueue farmId={farmId} status="approved" />
        </TabsContent>

        <TabsContent value="rejected">
          <RequestQueue farmId={farmId} status="rejected" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
